/**
 * "Create Well Testing Job" on an accepted Well Testing quote (WT-5b,
 * fieldiq-api#1258).
 *
 * Pipeline's Create Pipeline Job with a WELL where Pipeline asks for a site
 * (wt_jobs.well_id is NOT NULL). The well list is the quote customer's wells
 * (GET /well-testing/wells?customerId=). The day is optional and becomes the
 * job's planned start.
 *
 * A press with no well is SAID, not sent: the API's WELL_REQUIRED sentence, in
 * the quote screen's refusal notice. Every API refusal goes the same way
 * (wtQuoteRefusal), and success hands the job to the screen, which opens it —
 * a repeat (alreadyCreated) opens the job that already exists.
 *
 * Shown by QuoteDetailScreen only on an accepted Well Testing quote, only to
 * admin and office staff (the door's requireRole), and only once
 * created-records says the quote has no Well Testing job yet.
 */
import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle, Loader2 } from 'lucide-react';
import { api } from '../../api/client';
import { useWtWells } from '../../hooks/useWellTesting';
import { wtQuoteRefusal } from '../../lib/wt-quotes';
import type { CreateWtJobResponse } from '../../types/quote-pricing';

const SELECT = 'bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-sm text-white max-w-[16rem]';
const ACTION_PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:opacity-50 disabled:cursor-not-allowed';

export function CreateWtJobAction({
  quoteId,
  customerId,
  onCreated,
  onRefused,
}: {
  quoteId: string;
  customerId: string | null | undefined;
  onCreated: (data: CreateWtJobResponse['data']) => void;
  onRefused: (refusal: { code?: string; error: string }) => void;
}) {
  const [wellId, setWellId] = useState('');
  const [day, setDay] = useState('');

  const create = useMutation({
    mutationFn: () => api.quotes.createWtJob(quoteId, {
      wellId,
      ...(day ? { scheduledDate: day } : {}),
    }),
    onSuccess: (res) => {
      const d = res.data?.data;
      if (d?.job?.id) onCreated(d);
    },
    onError: (err) => onRefused(wtQuoteRefusal(err, 'Failed to create the Well Testing job')),
  });

  const press = () => {
    // No customer on the quote: the API's QUOTE_CUSTOMER_REQUIRED is the reason, so ask it.
    if (!wellId && customerId) {
      onRefused(wtQuoteRefusal({ response: { status: 400, data: { code: 'WELL_REQUIRED' } } }, ''));
      return;
    }
    create.mutate();
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-2" data-testid="create-wt-job">
      {customerId && <WellPicker customerId={customerId} wellId={wellId} onWell={setWellId} />}
      <input
        type="date"
        aria-label="Well Testing job planned start (optional)"
        value={day}
        onChange={(e) => setDay(e.target.value)}
        className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-sm text-white"
      />
      <button onClick={press} disabled={create.isPending} className={ACTION_PRIMARY}>
        {create.isPending ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />}
        Create Well Testing Job
      </button>
    </span>
  );
}

/** The quote customer's wells. Mounted only with a customer. */
function WellPicker({ customerId, wellId, onWell }: { customerId: string; wellId: string; onWell: (id: string) => void }) {
  const wells = useWtWells({ customerId });
  const rows = wells.data?.data ?? [];
  const empty = wells.isLoading ? 'Loading wells…' : wells.isError ? 'Wells could not be loaded' : rows.length ? 'Choose the well…' : 'No wells for this customer';
  return (
    <select aria-label="Well Testing job well" value={wellId} onChange={(e) => onWell(e.target.value)} className={SELECT}>
      <option value="">{empty}</option>
      {rows.map((w) => {
        const ident = w.uwi || w.apiNumber;
        return (
          <option key={w.id} value={w.id}>
            {w.wellName}{ident ? ` (${ident})` : ''}
          </option>
        );
      })}
    </select>
  );
}
